import type { Segment } from '@nono/contract';
import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';
import { orderIsoRange, osloDay, shiftIsoDay } from './reporting-days';

/*
 * Global filter state: segment + date range. The plan's end-state is to hold
 * these in TanStack Router search params (shareable, bookmarkable). For the
 * scaffold we use a context; migrating to URL state is a localized change.
 */

interface FiltersValue {
  segment: Segment;
  setSegment: (s: Segment) => void;
  from: string;
  to: string;
  setRange: (from: string, to: string) => void;
}

const FiltersContext = createContext<FiltersValue | null>(null);

export function FiltersProvider({ children }: { children: ReactNode }) {
  const [segment, setSegment] = useState<Segment>('all');
  // Siste sju dager TIL OG MED i dag — «kom det noe inn i dag?» er det første
  // eieren spør om når han åpner dashbordet.
  const [from, setFrom] = useState<string>(() => shiftIsoDay(osloDay(), -6));
  const [to, setTo] = useState<string>(() => osloDay());

  const value = useMemo<FiltersValue>(
    () => ({
      segment,
      setSegment,
      from,
      to,
      setRange: (f, t) => {
        const safe = orderIsoRange(f, t);
        setFrom(safe.from);
        setTo(safe.to);
      },
    }),
    [segment, from, to],
  );

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export function useFilters(): FiltersValue {
  const ctx = useContext(FiltersContext);
  if (!ctx) throw new Error('useFilters must be used within <FiltersProvider>');
  return ctx;
}
